import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./MetaphorExercisesSection.module.css";
import { useNavigate } from "react-router-dom";

function normalizeSrc(src) {
  if (!src) return null;
  if (typeof src === "string") return src;
  if (typeof src === "object") {
    return src.default || src.src || src.publicURL || null;
  }
  return null;
}

export default function MetaphorExercisesSection({
  insights = {},
  premiumPayload = {},
  archetypeKey = "",
  comicsBasePath = "/comics/pt",
  canViewPremium = false,
}) {
  const { t } = useTranslation("user");
  const navigate = useNavigate();
  const metaphor = insights?.archetype?.metaphor || "A bússola dos valores";
  const exercises = premiumPayload?.actExercises || [];

  const [manifest, setManifest] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [manifestError, setManifestError] = useState(null);

  useEffect(() => {
    if (premiumPayload && premiumPayload.manifest) {
      const m = JSON.parse(JSON.stringify(premiumPayload.manifest));
      m.pages = (m.pages || []).map((p) => ({
        thumb: normalizeSrc(p.thumb),
        full: normalizeSrc(p.full),
      }));
      m.cover =
        normalizeSrc(m.cover) || (m.pages[0] && m.pages[0].thumb) || null;
      setManifest(m);
    }
  }, [premiumPayload]);

  useEffect(() => {
    if (manifest) return;
    if (!archetypeKey) return;
    const url = `${comicsBasePath}/${archetypeKey}/manifest.json`;
    setLoadingManifest(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Manifest not found");
        return res.json();
      })
      .then((m) => {
        const pages = (m.pages || []).map((p) => {
          const thumb =
            p.thumb && (p.thumb.startsWith("http") || p.thumb.startsWith("/"))
              ? p.thumb
              : `${comicsBasePath}/${archetypeKey}/${p.thumb}`;
          const full =
            p.full && (p.full.startsWith("http") || p.full.startsWith("/"))
              ? p.full
              : `${comicsBasePath}/${archetypeKey}/${p.full}`;
          return { thumb, full };
        });
        const cover =
          m.cover && (m.cover.startsWith("http") || m.cover.startsWith("/"))
            ? m.cover
            : (pages[0] && pages[0].thumb) ||
              `${comicsBasePath}/${archetypeKey}/${m.cover || ""}`;
        setManifest({ ...m, pages, cover });
        setLoadingManifest(false);
      })
      .catch((err) => {
        setManifestError(err.message || String(err));
        setLoadingManifest(false);
      });
  }, [archetypeKey, comicsBasePath, manifest]);

  const legacyPages = useMemo(() => {
    const raw = Array.isArray(premiumPayload?.comicPages)
      ? premiumPayload.comicPages
      : null;
    if (!raw) return null;
    const pages = raw.map((item) => {
      const s = normalizeSrc(item);
      return { thumb: s, full: s };
    });
    return pages;
  }, [premiumPayload]);

  const pages = manifest ? manifest.pages : legacyPages || [];
  const coverSrc =
    normalizeSrc(premiumPayload?.coverImage) ||
    (manifest && manifest.cover) ||
    (pages[0] && pages[0].thumb) ||
    null;

  const [open, setOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const modalRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setTimeout(() => modalRef.current && modalRef.current.focus(), 50);
    } else {
      document.body.style.overflow = "";
      if (openerRef.current) openerRef.current.focus();
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight")
        setPageIndex((p) => Math.min(p + 1, (pages && pages.length - 1) || 0));
      if (e.key === "ArrowLeft") setPageIndex((p) => Math.max(p - 1, 0));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pages]);

  useEffect(() => {
    // pré-carrega as imagens vizinhas quando o modal está aberto
    if (!open || !pages || pages.length === 0) return;

    const toPreload = [pageIndex - 1, pageIndex + 1].filter(
      (i) => i >= 0 && i < pages.length
    );
    const images = [];

    toPreload.forEach((i) => {
      const url = pages[i]?.full;
      if (url) {
        const img = new Image();
        img.src = url;
        images.push(img);
      }
    });

    // opcional: retorna cleanup liberando referências (garantia GC)
    return () => {
      images.forEach((img) => {
        // remover src ajuda GC em alguns ambientes
        try {
          img.src = "";
        } catch (e) {}
      });
    };
  }, [open, pageIndex, pages]);

  function openModalAt(idx = 0) {
    setPageIndex(idx);
    setOpen(true);
  }
  function next() {
    setPageIndex((p) => Math.min(p + 1, (pages && pages.length - 1) || 0));
  }
  function prev() {
    setPageIndex((p) => Math.max(p - 1, 0));
  }

  return (
    <section className={styles.reportSection}>
      <header className={styles.header}>
        <h3>{t("userDashboard.metaphorTitle")}</h3>
      </header>
      <div className={styles.topRow}>
        <div className={styles.metaphorBox}>
          <p>
            {/* <strong>Metáfora sugerida:</strong> {metaphor} */}
            <strong>{t("userDashboard.metaphorSubtitle1")}</strong>
            {t("userDashboard.metaphorSubtitle2")}
          </p>

          <div className={styles.coverWrap}>
            {coverSrc ? (
              <img
                src={coverSrc}
                alt={`Capa: ${metaphor}`}
                className={styles.coverImage}
                onClick={() => openModalAt(0)}
                ref={openerRef}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") openModalAt(0);
                }}
              />
            ) : (
              <div className={styles.coverPlaceholder}>
                {t("userDashboard.noMetaphor")}
              </div>
            )}

            {!canViewPremium ? (
              <button
                className={styles.openBtn}
                onClick={() => navigate("/pricing")}
              >
                {t("userDashboard.premiumReportButton1")}
                <br />
                {t("userDashboard.premiumReportButton2")}
              </button>
            ) : (
              <button className={styles.openBtn} onClick={() => openModalAt(0)}>
                {t("userDashboard.premiumReportButton3")}
              </button>
            )}
          </div>

          {loadingManifest && (
            <p className={styles.hintSmall}>
              {t("userDashboard.loadingComic")}
            </p>
          )}
          {manifestError && (
            <p className={styles.hintSmall} style={{ color: "crimson" }}>
              {t("userDashboard.errorComic")}
              {manifestError}
            </p>
          )}
          {!loadingManifest && !manifest && !legacyPages && (
            <p className={styles.hintSmall}>
              {t("userDashboard.comicNotFound")}
            </p>
          )}
        </div>

        <div className={styles.exercisesBox}>
          <h4>{t("userDashboard.exercisesTitle")}</h4>
          {exercises.length === 0 ? (
            <p className={styles.empty}>{t("userDashboard.noExercises")}</p>
          ) : (
            <ol className={styles.exList}>
              {exercises.map((ex, i) => (
                <li key={i} className={styles.exItem}>
                  <strong>{ex.title}</strong> — {ex.description}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {open && (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={`Quadrinho: ${metaphor}`}
        >
          <div className={styles.modal} ref={modalRef} tabIndex={-1}>
            <div className={styles.modalHeader}>
              <div>
                {/* <strong>{metaphor}</strong> */}
                {/* <strong>Quem dirige sua vida</strong> */}
                <div className={styles.modalPager}>
                  {t("userDashboard.metaphorPage")}
                  {pageIndex + 1} / {(pages && pages.length) || 0}
                </div>
              </div>
              <button
                aria-label="Fechar"
                className={styles.closeBtn}
                onClick={() => setOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {pages && pages.length > 0 ? (
                <img
                  src={pages[pageIndex].full}
                  alt={`Página ${pageIndex + 1}`}
                  className={styles.modalImage}
                />
              ) : (
                <div className={styles.modalPlaceholder}>
                  {t("userDashboard.comicNotFound")}
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <div className={styles.navGroup}>
                <button
                  className={styles.navBtn}
                  onClick={prev}
                  disabled={pageIndex === 0}
                  aria-label="Página anterior"
                >
                  {t("userDashboard.metaphorNext")}
                </button>
                <button
                  className={styles.navBtn}
                  onClick={next}
                  disabled={!pages || pageIndex === pages.length - 1}
                  aria-label="Próxima página"
                >
                  {t("userDashboard.metaphorPrev")}
                </button>
              </div>

              <div className={styles.thumbs}>
                {pages &&
                  pages.map((p, idx) => (
                    <button
                      key={idx}
                      className={`${styles.thumbBtn} ${
                        idx === pageIndex ? styles.thumbActive : ""
                      }`}
                      onClick={() => setPageIndex(idx)}
                      aria-label={`Ir para página ${idx + 1}`}
                    >
                      <img src={p.thumb} alt={`Miniatura ${idx + 1}`} />
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
